import React from 'react';
import { createRoot } from 'react-dom/client';

import {
  CUSTOM_WORKFLOW,
  NON_PACKAGED,
  PACKAGED,
} from 'pages/IntegrationManager/data';
import { getPerson } from 'actions/loggedInUser';
import { initializeSessionService } from 'services/sessionService';
import configureStore, { getGlobalStore, exposeStoreToGwt } from 'store';
import lazyLoad from 'utils/lazyLoad';
import { configureSentryErrorFilters } from 'utils/sentry';
import ApplicationLoadingError from 'Application/ApplicationShell/ApplicationLoadingError';
import {
  initializeApp,
  initializeEmbeddedApp,
} from 'actions/application/initialization';
import {
  getEBUserId,
  getEBUsername,
  setEBSGlobalContext,
} from '@xmatters/common/context/ebsGlobalContext';
import { setEmbeddedAppPath } from 'utils/navigation/navigation';
import {
  setDefaultContainerGetter,
  getPopoverContainer,
} from 'utils/appLayout';
import AppWrapper from './AppWrapper';

async function runInitialDispatches(globalStore) {
  if (window.XM_REDUX_ACTIONS_QUEUE) {
    await Promise.all(
      window.XM_REDUX_ACTIONS_QUEUE.map(action => globalStore.dispatch(action)),
    );
  }
  window.XM_REDUX_ACTIONS_QUEUE = null;
}

export function renderAppOnPage({
  appComponent: Application,
  globalStore = getGlobalStore(),
  element = document.getElementById('reactPage'),
  sparkConfig,
}) {
  createRoot(element).render(
    <AppWrapper sparkConfig={sparkConfig} globalStore={globalStore}>
      <Application />
    </AppWrapper>,
  );
}

window.XM_isReact17 = false;
const reactRoots = new WeakMap();

const getInjectReactComponent =
  ({ globalStore, getLazyReactComponent, sparkConfig }) =>
  (componentName, selector, parameters) => {
    function renderLazyComponent(loader) {
      const element = document.querySelector(selector);
      if (!element) {
        return;
      }

      if (!reactRoots.has(element)) {
        reactRoots.set(element, createRoot(element));
      }

      const LoadableComponent = lazyLoad(loader);

      reactRoots.get(element).render(
        <AppWrapper globalStore={globalStore} sparkConfig={sparkConfig}>
          <LoadableComponent {...parameters} />
        </AppWrapper>,
      );
    }

    setTimeout(() => {
      const Component = getLazyReactComponent(componentName);
      renderLazyComponent(Component.dynamic.loader);
    }, 0);
  };

export function initializeApplication({
  getLazyReactComponent,
  appComponent,
  sparkConfig = { getPopoverContainer: () => getPopoverContainer(null) },
}) {
  if (window.bundleLocation) {
    // @ts-ignore
    // eslint-disable-next-line no-undef
    __webpack_public_path__ = window.bundleLocation;
  }

  configureSentryErrorFilters();

  const globalStore = configureStore();
  exposeStoreToGwt(globalStore);

  window.XM_GET_PERSON = getPerson;

  window.XM_unmountReactApp = selector => {
    const element = document.querySelector(selector);
    if (!element || !reactRoots.has(element)) {
      return;
    }
    const rootToUnmount = reactRoots.get(element);
    reactRoots.delete(element);
    // async to make react 18 happy
    setTimeout(() => {
      rootToUnmount.unmount();
    }, 0);
  };

  const injectReactComponent = getInjectReactComponent({
    globalStore,
    getLazyReactComponent,
    sparkConfig,
  });

  // unfold is defined under alarmpoint-body.xsl @ line 276
  if (window.XM_injectReactApp && window.XM_injectReactApp.unfold) {
    window.XM_injectReactApp.unfold(injectReactComponent);
  } else {
    window.XM_injectReactApp = injectReactComponent;
  }

  window.XM_START_REACT = async () => {
    try {
      await runInitialDispatches(globalStore);
      await globalStore.dispatch(initializeApp());
      renderAppOnPage({ appComponent, sparkConfig });
    } catch (e) {
      renderAppOnPage({
        appComponent: ApplicationLoadingError,
        sparkConfig,
      });
    }
  };

  if (window.XM_GWT_READY_FOR_REACT) {
    window.XM_START_REACT();
  }

  window.XM_integrationInfo = JSON.stringify({
    NON_PACKAGED,
    PACKAGED,
    CUSTOM_WORKFLOW,
  });

  initializeSessionService(globalStore);
}

export async function initializeEBSEmbeddedApp({
  embeddedAppPath,
  globalContext,
}) {
  setEBSGlobalContext(globalContext);
  setEmbeddedAppPath(embeddedAppPath);
  setDefaultContainerGetter(() =>
    document.querySelector("#main [data-theme='everbridge']"),
  );
  const globalStore = configureStore();
  await globalStore.dispatch(
    initializeEmbeddedApp({
      loggedInUserId: getEBUsername() || getEBUserId(),
    }),
  );
}
